/**
 * Resolved insurance mailing address for clipboard copy (matches InsuranceAddress precedence).
 * Prefers case-specific `location` when set; otherwise carrier default contact.
 */
export function getInsuranceLocationContact(data, folder, activeCase) {
    let row = data
    if (activeCase) {
        if (folder === "worker_comp") {
            row = activeCase.worker_comp ?? data
        } else {
            const list = activeCase[folder]
            if (Array.isArray(list)) {
                const found = list.find((item) => item.id == data.id)
                if (found) {
                    row = found
                }
            }
        }
    }

    const loc = row?.location?.contact_information
    if (loc && (loc.address_1 || loc.city || loc.state || loc.zip)) {
        return {
            address1: loc.address_1 ?? "",
            city: loc.city ?? "",
            state: loc.state ?? "",
            zip: loc.zip ?? "",
        }
    }

    const carrier = row?.insurance_carrier?.contact_information
    return {
        address1: carrier?.address_1 ?? "",
        city: carrier?.city ?? "",
        state: carrier?.state ?? "",
        zip: carrier?.zip ?? "",
    }
}

/** Most recent note body for a tab’s `note_type_id` (same ordering as NotesVertical). */
export function getLastNoteForType(notes, noteTypeId) {
    if (noteTypeId == null || !notes?.length) {
        return ""
    }
    const sorted = [...notes].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    const hit = sorted.find((n) => n.note_type_id == noteTypeId)
    return hit?.note ?? ""
}

/** Whether a value should appear on a copy line (allows `0`; omits null/undefined/blank strings). */
export function hasCopyValue(value) {
    if (value === true || value === false) {
        return true
    }
    if (value == null) {
        return false
    }
    return String(value).trim() !== ""
}

/** `Label: value` or null when there is nothing to copy. */
export function copyLine(label, value) {
    if (!hasCopyValue(value)) {
        return null
    }
    return `${label}: ${String(value).trim()}`
}

/** Join copy lines; drops null/empty strings; keeps section headers (no `:` required). */
export function joinCopyLines(lines) {
    return lines.filter((line) => line != null && line !== "").join("\n")
}

/** One line: `Label: INN … / OON …` (omits missing sides; whole line omitted if both empty). */
export function copyLineInnOon(label, innVal, oonVal) {
    const parts = []
    if (hasCopyValue(innVal)) {
        parts.push(`INN ${String(innVal).trim()}`)
    }
    if (hasCopyValue(oonVal)) {
        parts.push(`OON ${String(oonVal).trim()}`)
    }
    if (!parts.length) {
        return null
    }
    return `${label}: ${parts.join(" / ")}`
}

/** Section divider used in legacy insurance copy-note templates. */
export const COPY_SECTION_SEPARATOR = "_____________________________________"

/** Checkbox value for clipboard copy (`Yes` / `No`). */
export function formatCopyCheckbox(value) {
    return value === true || value === 1 || value === "1" ? "Yes" : "No"
}

/** Always emits a line; empty value is `Label:` only (no dash or placeholder). */
export function copyLineAlways(label, value) {
    return hasCopyValue(value) ? `${label}: ${String(value).trim()}` : `${label}:`
}

/** Always emits a line with a Yes/No checkbox value. */
export function copyLineCheckboxAlways(label, value) {
    return `${label}: ${formatCopyCheckbox(value)}`
}

/** Always emits a line; legacy dollar labels concatenate directly (e.g. `MP-$5000` or `MP-$`). */
export function copyDollarLineAlways(prefix, value) {
    return hasCopyValue(value) ? `${prefix}${String(value).trim()}` : prefix
}

/** Always emits a line; only sides with values (no `-` / em dash for missing). */
export function copyLineInnOonAlways(label, innVal, oonVal) {
    const parts = []
    if (hasCopyValue(innVal)) {
        parts.push(`${String(innVal).trim()}`)
    }
    if (hasCopyValue(oonVal)) {
        parts.push(`${String(oonVal).trim()}`)
    }
    return parts.length ? `${label}: ${parts.join(" / ")}` : `${label}:`
}
